import type { SourceType } from '@shared/contract';

/** Inspelningen skickas som WAV, 16 kHz mono, 16 bitar. Samma format som servern transkriberar. */
export const SAMPLE_RATE = 16000;
export const SEGMENT_MS = 8000;
const CODEC = 'audio/wav';

/**
 * Webbläsarens brusreducering och ekosläckning är gjorda för samtal med en person nära
 * mikrofonen och kan dämpa röster längre bort i ett mötesrum. Appen spelar inte upp ljud,
 * så ekosläckning behövs inte. Automatisk nivå behålls, den hjälper svaga röster.
 */
const MIC_PROCESSING = { echoCancellation: false, noiseSuppression: false, autoGainControl: true };

export function detectSourceType(): SourceType {
  const ua = navigator.userAgent;
  const iOS = /iPhone|iPad|iPod/.test(ua) || (/Macintosh/.test(ua) && navigator.maxTouchPoints > 1);
  return iOS ? 'browser_iphone' : 'browser_mac';
}

export function isIOS(): boolean {
  return detectSourceType() === 'browser_iphone';
}

export async function listMicrophones(): Promise<MediaDeviceInfo[]> {
  if (!navigator.mediaDevices?.enumerateDevices) return [];
  const devices = await navigator.mediaDevices.enumerateDevices();
  return devices.filter((d) => d.kind === 'audioinput');
}

export interface RecordedSegment {
  sequence_number: number;
  started_at: string;
  offset_ms: number;
  duration_ms: number;
  codec: string;
  sample_rate: number;
  channels: number;
  device_name: string;
  data: ArrayBuffer;
}

export interface RecorderCallbacks {
  onSegment: (segment: RecordedSegment) => void | Promise<void>;
  onLevel?: (level: number) => void;
  onError?: (message: string) => void;
  /** Inga ljudprover har kommit sedan `since` (ms sedan epoch), fast inspelningen pågår. */
  onStall?: (since: number) => void;
  /** Ljudet kommer igen efter ett avbrott från `since` till `until`. */
  onRecover?: (since: number, until: number) => void;
}

/** Så länge utan ljudprover räknas som avbrott. Workleten skickar ett block var 100:e ms. */
const STALL_MS = 3000;
/** Hjälper inte resume() inom så lång tid öppnas mikrofonen på nytt. */
const REOPEN_AFTER_MS = 5000;

type AudioContextCtor = typeof AudioContext;

function audioContextCtor(): AudioContextCtor | null {
  return window.AudioContext ?? (window as unknown as { webkitAudioContext?: AudioContextCtor }).webkitAudioContext ?? null;
}

/**
 * Spelar in obrutet ljud som rå PCM via en AudioWorklet och delar det i segment om
 * exakt SEGMENT_MS. Segmenten ligger sampel för sampel efter varandra, utan glapp,
 * så att servern kan sätta ihop hela mötet igen för den slutliga transkriberingen.
 */
export class SegmentRecorder {
  private stream: MediaStream | null = null;
  private ctx: AudioContext | null = null;
  private nodes: AudioNode[] = [];
  private levelTimer: number | null = null;
  private active = false;
  private readonly buffer = new Int16Array((SAMPLE_RATE * SEGMENT_MS) / 1000);
  private filled = 0;
  private segmentWallClock = '';
  /** Segment lämnas till onSegment i tur och ordning. */
  private delivering: Promise<void> = Promise.resolve();
  private deviceId: string | undefined;
  private lastSampleAt = 0;
  private stalledSince: number | null = null;
  private reopenedAt = 0;
  private watchdog: number | null = null;
  deviceName = '';

  constructor(
    private nextSequence: number,
    private offsetMs: number,
    private readonly callbacks: RecorderCallbacks,
  ) {}

  get recordedMs(): number {
    return this.offsetMs + Math.round((this.filled * 1000) / SAMPLE_RATE);
  }

  get sequence(): number {
    return this.nextSequence;
  }

  get isActive(): boolean {
    return this.active;
  }

  async open(deviceId?: string): Promise<void> {
    if (!navigator.mediaDevices?.getUserMedia) {
      throw new Error('Webbläsaren saknar stöd för mikrofon, eller sidan är inte öppnad via HTTPS.');
    }
    const Ctx = audioContextCtor();
    if (!Ctx || typeof AudioWorkletNode === 'undefined') {
      throw new Error('Webbläsaren saknar stöd för ljudinspelning (AudioWorklet). Uppdatera webbläsaren.');
    }
    this.deviceId = deviceId;
    this.stream = await navigator.mediaDevices.getUserMedia({
      audio: { deviceId: deviceId ? { exact: deviceId } : undefined, ...MIC_PROCESSING, channelCount: 1 },
    });
    const track = this.stream.getAudioTracks()[0];
    this.deviceName = track?.label || 'Mikrofon';
    track?.addEventListener('ended', () => {
      if (!this.active) {
        this.callbacks.onError?.('Mikrofonen kopplades bort eller stängdes av systemet.');
        return;
      }
      // Under inspelning öppnas mikrofonen igen direkt, annars spelas bara tystnad in.
      this.reopenedAt = Date.now();
      void this.reopen().then((ok) => {
        if (ok) this.callbacks.onError?.('Mikrofonen kopplades bort och har startats igen.');
      });
    });
    track?.addEventListener('mute', () => {
      this.callbacks.onError?.('Mikrofonen tystades av systemet. Kontrollera att sidan är aktiv.');
    });

    // Helst går kontexten direkt i 16 kHz. Vissa webbläsare tillåter inte det med
    // mikrofonljud; då används standardtakten och workleten samplar om.
    try {
      await this.connect(new Ctx({ sampleRate: SAMPLE_RATE }));
    } catch {
      await this.disconnect();
      await this.connect(new Ctx());
    }
  }

  private async connect(ctx: AudioContext): Promise<void> {
    this.ctx = ctx;
    // Systemet kan stänga av ljudkontexten (viloläge, byte av ljudenhet). Under inspelning startas den igen.
    ctx.onstatechange = () => {
      if (this.active && this.ctx === ctx && ctx.state !== 'running' && ctx.state !== 'closed') void ctx.resume().catch(() => undefined);
    };
    await ctx.audioWorklet.addModule(`${import.meta.env.BASE_URL}pcm-capture.js`);
    const source = ctx.createMediaStreamSource(this.stream!);
    const capture = new AudioWorkletNode(ctx, 'pcm-capture', {
      numberOfInputs: 1,
      numberOfOutputs: 1,
      channelCount: 1,
      channelCountMode: 'explicit',
    });
    capture.port.onmessage = (e: MessageEvent<Float32Array>) => this.onSamples(e.data);
    // Workleten måste vara kopplad till högtalarutgången för att köras. Volymen är noll.
    const mute = ctx.createGain();
    mute.gain.value = 0;
    source.connect(capture);
    capture.connect(mute);
    mute.connect(ctx.destination);
    this.nodes = [source, capture, mute];
    if (this.callbacks.onLevel) {
      const analyser = ctx.createAnalyser();
      analyser.fftSize = 512;
      source.connect(analyser);
      this.nodes.push(analyser);
      const buf = new Uint8Array(analyser.fftSize);
      this.levelTimer = window.setInterval(() => {
        analyser.getByteTimeDomainData(buf);
        let sum = 0;
        for (const v of buf) sum += ((v - 128) / 128) ** 2;
        this.callbacks.onLevel?.(Math.min(1, Math.sqrt(sum / buf.length) * 4));
      }, 150);
    }
  }

  private async disconnect(): Promise<void> {
    if (this.levelTimer !== null) clearInterval(this.levelTimer);
    this.levelTimer = null;
    this.nodes.forEach((n) => n.disconnect());
    this.nodes = [];
    await this.ctx?.close().catch(() => undefined);
    this.ctx = null;
  }

  private onSamples(samples: Float32Array): void {
    if (!this.active) return;
    this.lastSampleAt = Date.now();
    if (this.stalledSince !== null) {
      const since = this.stalledSince;
      this.stalledSince = null;
      this.callbacks.onRecover?.(since, this.lastSampleAt);
    }
    for (let i = 0; i < samples.length; i++) {
      if (this.filled === 0) this.segmentWallClock = new Date().toISOString();
      const v = Math.max(-1, Math.min(1, samples[i]));
      this.buffer[this.filled++] = v < 0 ? v * 0x8000 : v * 0x7fff;
      if (this.filled === this.buffer.length) this.emit();
    }
  }

  /** Lämnar det som spelats in sedan förra segmentet som ett eget segment. */
  private emit(): void {
    if (!this.filled) return;
    const pcm = this.buffer.slice(0, this.filled);
    const segment: RecordedSegment = {
      sequence_number: this.nextSequence++,
      started_at: this.segmentWallClock,
      offset_ms: this.offsetMs,
      duration_ms: Math.round((pcm.length * 1000) / SAMPLE_RATE),
      codec: CODEC,
      sample_rate: SAMPLE_RATE,
      channels: 1,
      device_name: this.deviceName,
      data: pcmToWav(pcm),
    };
    this.offsetMs += segment.duration_ms;
    this.filled = 0;
    this.delivering = this.delivering
      .then(() => this.callbacks.onSegment(segment))
      .catch((err) => this.callbacks.onError?.(`Kunde inte spara ljudsegment: ${(err as Error).message}`));
  }

  /** Startar (eller återupptar) inspelningen. */
  start(): void {
    if (!this.stream || !this.ctx) throw new Error('Mikrofonen är inte öppnad.');
    if (this.active) return;
    this.active = true;
    this.lastSampleAt = Date.now();
    this.stalledSince = null;
    void this.ctx.resume();
    this.watchdog = window.setInterval(() => void this.checkStall(), 1000);
  }

  /** Pausar: det som spelats in sparas som ett segment och inget mer tas emot. */
  async pause(): Promise<void> {
    if (!this.active) return;
    this.active = false;
    this.stopWatchdog();
    this.stalledSince = null;
    this.emit();
    await this.delivering;
  }

  private stopWatchdog(): void {
    if (this.watchdog !== null) clearInterval(this.watchdog);
    this.watchdog = null;
  }

  /**
   * Vakten: kommer inga ljudprover fast inspelningen pågår (datorn har sovit, ljudkontexten
   * stängts av eller mikrofonen försvunnit) sparas det som spelats in som ett eget segment,
   * så att nästa segment får rätt starttid, och ljudet startas om.
   */
  private async checkStall(): Promise<void> {
    if (!this.active) return;
    const now = Date.now();
    if (now - this.lastSampleAt < STALL_MS) return;
    if (this.stalledSince === null) {
      this.stalledSince = this.lastSampleAt;
      this.emit();
      this.callbacks.onStall?.(this.stalledSince);
    }
    void this.ctx?.resume().catch(() => undefined);
    const trackLive = this.stream?.getAudioTracks()[0]?.readyState === 'live';
    if ((!trackLive || now - this.stalledSince >= REOPEN_AFTER_MS) && now - this.reopenedAt >= REOPEN_AFTER_MS) {
      this.reopenedAt = now;
      await this.reopen();
    }
  }

  /** Öppnar mikrofonen och ljudkontexten på nytt utan att avbryta segmentnumreringen. */
  private async reopen(): Promise<boolean> {
    await this.disconnect();
    this.stream?.getTracks().forEach((t) => t.stop());
    this.stream = null;
    // Den valda mikrofonen kan saknas efter viloläge; då provas standardmikrofonen.
    for (const id of this.deviceId ? [this.deviceId, undefined] : [undefined]) {
      try {
        await this.open(id);
        if (this.active) await this.ctx?.resume();
        return true;
      } catch {
        await this.disconnect();
      }
    }
    this.callbacks.onError?.('Mikrofonen kunde inte startas igen. Kontrollera mikrofonen och klicka på Pausa och sedan Återuppta.');
    return false;
  }

  /** Stoppar och släpper mikrofonen. */
  async close(): Promise<void> {
    await this.pause();
    this.stopWatchdog();
    await this.disconnect();
    this.stream?.getTracks().forEach((t) => t.stop());
    this.stream = null;
  }
}

function pcmToWav(pcm: Int16Array): ArrayBuffer {
  const out = new ArrayBuffer(44 + pcm.byteLength);
  const v = new DataView(out);
  const ascii = (offset: number, s: string) => [...s].forEach((c, i) => v.setUint8(offset + i, c.charCodeAt(0)));
  ascii(0, 'RIFF');
  v.setUint32(4, 36 + pcm.byteLength, true);
  ascii(8, 'WAVE');
  ascii(12, 'fmt ');
  v.setUint32(16, 16, true);
  v.setUint16(20, 1, true);
  v.setUint16(22, 1, true);
  v.setUint32(24, SAMPLE_RATE, true);
  v.setUint32(28, SAMPLE_RATE * 2, true);
  v.setUint16(32, 2, true);
  v.setUint16(34, 16, true);
  ascii(36, 'data');
  v.setUint32(40, pcm.byteLength, true);
  new Int16Array(out, 44).set(pcm);
  return out;
}
